import React from 'react';
import {
  Modal,
  View,
  Text,
  TouchableOpacity,
  StyleSheet,
  Linking,
  Alert,
  Platform,
  Clipboard,
  Share,
  ScrollView,
  useWindowDimensions,
} from 'react-native';
import QRCode from 'react-native-qrcode-svg';

export interface FrontDeskQRModalProps {
  visible: boolean;
  onClose: () => void;
  /** URL encoded in the QR (smart booking page `/b/:id?...`). */
  bookingUrl: string;
  /** Printable PDF sign (download / print). */
  signPdfUrl: string;
  businessName: string;
}

const isNativeMobile = Platform.OS === 'ios' || Platform.OS === 'android';

const FrontDeskQRModal: React.FC<FrontDeskQRModalProps> = ({
  visible,
  onClose,
  bookingUrl,
  signPdfUrl,
  businessName,
}) => {
  const { width: windowWidth } = useWindowDimensions();
  const useStackLayout = isNativeMobile || windowWidth < 520;
  const handleDownloadPrint = async () => {
    if (!signPdfUrl) return;
    try {
      if (Platform.OS === 'web' && typeof window !== 'undefined') {
        window.open(signPdfUrl, '_blank', 'noopener,noreferrer');
        return;
      }
      const canOpen = await Linking.canOpenURL(signPdfUrl);
      if (!canOpen) {
        Alert.alert('Error', 'Could not open the printable sign on this device.');
        return;
      }
      await Linking.openURL(signPdfUrl);
    } catch {
      Alert.alert('Error', 'Could not open download/print link right now.');
    }
  };

  const handleCopyLink = () => {
    if (!bookingUrl) return;
    Clipboard.setString(bookingUrl);
    Alert.alert('Copied', 'Booking link copied. Paste it in emails, texts, or your website.');
  };

  const handleShareLink = async () => {
    if (!bookingUrl) return;
    const label = businessName || 'our business';
    const message = `Book an appointment with ${label} on BookAPT:\n${bookingUrl}`;
    try {
      if (Platform.OS === 'web' && typeof navigator !== 'undefined' && navigator.share) {
        await navigator.share({ title: `Book with ${label}`, text: message, url: bookingUrl });
        return;
      }
      if (isNativeMobile) {
        await Share.share(
          Platform.OS === 'ios'
            ? { message, url: bookingUrl, title: `Book with ${label}` }
            : { message, title: `Book with ${label}` }
        );
        return;
      }
      handleCopyLink();
    } catch (error: any) {
      if (error?.message !== 'User did not share') {
        handleCopyLink();
      }
    }
  };

  return (
    <Modal visible={visible} animationType="slide" transparent>
      <View style={styles.overlay}>
        <ScrollView
          contentContainerStyle={styles.scrollContent}
          keyboardShouldPersistTaps="handled"
          bounces={false}
        >
          <View style={[styles.card, useStackLayout && styles.cardMobile]}>
            <Text style={styles.title}>Front desk booking QR</Text>
            <Text style={styles.subtitle}>
              Scan to book appointments with {businessName || 'your business'}
            </Text>
            <Text style={styles.hint}>
              This QR opens your smart booking link: phones try the BookAPT app (then the store); computers go straight to the web app.
            </Text>

            {bookingUrl ? (
              <View style={styles.linkBox}>
                <Text style={styles.linkLabel}>Your booking link</Text>
                <Text selectable style={styles.linkText}>
                  {bookingUrl}
                </Text>
              </View>
            ) : null}

            <View style={[styles.bodyRow, useStackLayout && styles.bodyRowMobile]}>
              <View style={styles.qrColumn}>
                {bookingUrl ? (
                  <View style={styles.qrBox}>
                    <QRCode
                      value={bookingUrl}
                      size={useStackLayout ? 220 : 200}
                      color="#000000"
                      backgroundColor="#FFFFFF"
                    />
                  </View>
                ) : null}
              </View>

              <View style={[styles.sideButtons, useStackLayout && styles.sideButtonsMobile]}>
                <TouchableOpacity
                  style={styles.sideButtonPrimary}
                  onPress={isNativeMobile ? handleShareLink : handleCopyLink}
                  activeOpacity={0.85}
                >
                  <Text style={styles.sideButtonPrimaryText}>
                    {isNativeMobile ? 'Share booking link' : 'Copy booking link'}
                  </Text>
                </TouchableOpacity>
                {isNativeMobile ? (
                  <TouchableOpacity
                    style={styles.sideButtonSecondary}
                    onPress={handleCopyLink}
                    activeOpacity={0.85}
                  >
                    <Text style={styles.sideButtonSecondaryText}>Copy link</Text>
                  </TouchableOpacity>
                ) : null}
                <TouchableOpacity
                  style={styles.sideButtonSecondary}
                  onPress={handleDownloadPrint}
                  activeOpacity={0.85}
                >
                  <Text style={styles.sideButtonSecondaryText}>Download / print sign</Text>
                </TouchableOpacity>
                <TouchableOpacity
                  style={styles.sideButtonSecondary}
                  onPress={onClose}
                  activeOpacity={0.85}
                >
                  <Text style={styles.sideButtonSecondaryText}>Close</Text>
                </TouchableOpacity>
              </View>
            </View>
          </View>
        </ScrollView>
      </View>
    </Modal>
  );
};

const styles = StyleSheet.create({
  overlay: {
    flex: 1,
    backgroundColor: 'rgba(0,0,0,0.45)',
    justifyContent: 'center',
  },
  scrollContent: {
    flexGrow: 1,
    justifyContent: 'center',
    alignItems: 'center',
    padding: 20,
  },
  card: {
    width: '100%',
    maxWidth: 400,
    backgroundColor: '#fff',
    borderRadius: 16,
    padding: 20,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 4 },
    shadowOpacity: 0.15,
    shadowRadius: 12,
    elevation: 8,
  },
  cardMobile: {
    maxWidth: '100%',
  },
  title: {
    fontSize: 18,
    fontWeight: '700',
    color: '#1C1C1E',
    marginBottom: 6,
  },
  subtitle: {
    fontSize: 15,
    color: '#4B5563',
    lineHeight: 21,
    marginBottom: 8,
  },
  hint: {
    fontSize: 12,
    color: '#6B7280',
    lineHeight: 17,
    marginBottom: 16,
  },
  linkBox: {
    backgroundColor: '#F9FAFB',
    borderWidth: 1,
    borderColor: '#E5E7EB',
    borderRadius: 10,
    padding: 12,
    marginBottom: 16,
  },
  linkLabel: {
    fontSize: 12,
    fontWeight: '600',
    color: '#374151',
    marginBottom: 6,
  },
  linkText: {
    fontSize: 12,
    color: '#4B5563',
    lineHeight: 18,
  },
  bodyRow: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: 16,
  },
  bodyRowMobile: {
    flexDirection: 'column',
    alignItems: 'stretch',
  },
  qrColumn: {
    flexShrink: 0,
    alignItems: 'center',
  },
  qrBox: {
    padding: 10,
    backgroundColor: '#FFFFFF',
    borderRadius: 12,
    borderWidth: 1,
    borderColor: '#E5E7EB',
  },
  sideButtons: {
    flex: 1,
    minWidth: 132,
    justifyContent: 'center',
    gap: 12,
  },
  sideButtonsMobile: {
    flex: 0,
    minWidth: 0,
    width: '100%',
  },
  sideButtonPrimary: {
    backgroundColor: '#7C3AED',
    paddingVertical: 14,
    paddingHorizontal: 16,
    borderRadius: 10,
    alignItems: 'center',
    justifyContent: 'center',
  },
  sideButtonPrimaryText: {
    color: '#fff',
    fontSize: 15,
    fontWeight: '600',
    textAlign: 'center',
  },
  sideButtonSecondary: {
    backgroundColor: '#F3F4F6',
    paddingVertical: 14,
    paddingHorizontal: 16,
    borderRadius: 10,
    borderWidth: 1,
    borderColor: '#E5E7EB',
    alignItems: 'center',
    justifyContent: 'center',
  },
  sideButtonSecondaryText: {
    color: '#374151',
    fontSize: 15,
    fontWeight: '600',
    textAlign: 'center',
  },
});

export default FrontDeskQRModal;
